import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import useApi from '../../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ACQUISITION_OPTIONS, Acquisition, JOB_MODE_OPTIONS, JobMode, JobScope, ListingPayloadFields, Routing, SCOPE_OPTIONS } from '../servicesShared';
import ServicePricing from '../ServicePricing';
import { DirtySetter, SectionHeader } from './editorShared';

/** The live (non-versioned) service fields: PUT /services/{id}, built by
 * listingPayload in servicesShared so the save body and this form can't
 * drift apart again. */
export type ListingFields = ListingPayloadFields;

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const EVENT_NAME_RE = /^[a-z0-9][a-z0-9._-]{0,127}$/;

/** Event names that start a job (services.start_on_events), with the org's
 * event catalog as suggestions. Routines decide what wakes an existing job;
 * this decides what creates one. */
const StartOnEvents: React.FC<{ value: string[]; onChange: (next: string[]) => void }> = ({ value, onChange }) => {
    const { get } = useApi();
    const [draft, setDraft] = useState('');
    const [known, setKnown] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await get('/service-events/definitions');
                const names = ((response.data?.data || response.data || []) as { name?: string }[]).map((d) => d.name).filter(Boolean) as string[];
                if (!cancelled) setKnown(names);
            } catch {
                /* suggestions only */
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const add = (name: string) => {
        const clean = name.trim().toLowerCase();
        if (!clean || !EVENT_NAME_RE.test(clean) || value.includes(clean)) return;
        onChange([...value, clean]);
        setDraft('');
    };
    const suggestions = known.filter((name) => !value.includes(name) && (!draft || name.includes(draft.trim().toLowerCase()))).slice(0, 8);

    return (
        <div className="space-y-2">
            {value.length > 0 && (
                <ul className="flex flex-wrap gap-1">
                    {value.map((name) => (
                        <li key={name} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono">
                            {name}
                            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => onChange(value.filter((item) => item !== name))} aria-label={`Remove ${name}`}>
                                <X className="h-3 w-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <Input
                value={draft}
                placeholder="trial.started"
                list="start-on-events-suggestions"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ',') {
                        event.preventDefault();
                        add(draft);
                    }
                }}
                onBlur={() => draft.trim() && add(draft)}
            />
            <datalist id="start-on-events-suggestions">
                {suggestions.map((name) => (
                    <option key={name} value={name} />
                ))}
            </datalist>
        </div>
    );
};

export const SaveIndicator: React.FC<{ state: SaveState }> = ({ state }) => (
    <span className="text-xs text-muted-foreground shrink-0 mt-1" aria-live="polite">
        {state === 'saving' && 'Saving…'}
        {state === 'saved' && 'Saved · live'}
        {state === 'error' && <span className="text-red-500">Not saved — see error</span>}
    </span>
);

/**
 * Listing: everything the customer sees. The public name and description, how
 * they get a job, the price, and whether new jobs can start. How the job
 * reaches the customer is not configured here: every message goes through the
 * customer agent chosen on Build, on the channels that agent has.
 */
export const ListingTab: React.FC<{
    serviceId: string;
    listing: ListingFields;
    onListingChange: (patch: Partial<ListingFields>) => void;
    saveState: SaveState;
    commerceEnabled: boolean;
    status: 'active' | 'disabled';
    onStatusChange: (next: 'active' | 'disabled') => void;
    jobsOn: number;
    onDelete: () => void;
    /** version.routing: saved with the draft, live with the next publish. */
    routing: Routing;
    onRoutingChange: DirtySetter<Routing>;
}> = ({ serviceId, listing, onListingChange, saveState, commerceEnabled, status, onStatusChange, jobsOn, onDelete, routing, onRoutingChange }) => {
    const acquisitionOptions = ACQUISITION_OPTIONS.filter((option) => option.value !== 'stripe' || commerceEnabled || listing.acquisition === 'stripe');

    return (
        <>
            <section className="mb-10">
                <SectionHeader
                    title="What customers see"
                    description="The public face of the service. Live the moment it saves; never part of a draft or version."
                    trailing={<SaveIndicator state={saveState} />}
                />
                <div className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <Label htmlFor="listing-internal">Internal name</Label>
                            <Input id="listing-internal" value={listing.internal_name} onChange={(event) => onListingChange({ internal_name: event.target.value })} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="listing-name">Public name</Label>
                            <Input id="listing-name" maxLength={120} value={listing.name} onChange={(event) => onListingChange({ name: event.target.value })} placeholder="Defaults to the internal name" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="listing-short">Short description</Label>
                        <Input
                            id="listing-short"
                            maxLength={300}
                            value={listing.short_description}
                            onChange={(event) => onListingChange({ short_description: event.target.value })}
                            placeholder="One line customers (and your agent) see"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="listing-description">Description</Label>
                        <Textarea
                            id="listing-description"
                            rows={3}
                            value={listing.description}
                            onChange={(event) => onListingChange({ description: event.target.value })}
                            placeholder="What the job owns for the customer, what it needs from them, what they get."
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <Switch checked={listing.visible} onCheckedChange={(checked) => onListingChange({ visible: checked })} />
                        <span className="flex flex-col">
                            <span>Visible to customers</span>
                            <span className="text-xs text-muted-foreground">Listed in services.list and offerable by your agent. Hidden services can still be assigned by your team.</span>
                        </span>
                    </label>
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="How your agent routes to it"
                    description="Read by your customer agent, never shown to customers. Saved with the draft and live with the next publish. Without it the agent only sees the job's name and may answer or escalate instead of handing the ask to the job."
                />
                <div className="space-y-6">
                    <div className="space-y-1">
                        <Label htmlFor="routing-handles">What a running job handles</Label>
                        <Textarea
                            id="routing-handles"
                            rows={3}
                            maxLength={1000}
                            value={routing.handles || ''}
                            onChange={(event) => onRoutingChange((prev) => ({ ...prev, handles: event.target.value }))}
                            placeholder="The asks the job owns once it is running, in plain words. When a customer's ask falls under this, the agent relays it to the job before answering itself or escalating to a person. Say what stays out (billing, account decisions, a request for a person)."
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="routing-offer">When to offer it</Label>
                        <Textarea
                            id="routing-offer"
                            rows={2}
                            maxLength={1000}
                            value={routing.offer_when || ''}
                            onChange={(event) => onRoutingChange((prev) => ({ ...prev, offer_when: event.target.value }))}
                            placeholder="The situation in which the agent should propose starting this service to a customer who has no job yet. Only applies to services customers can start themselves."
                        />
                    </div>
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader title="How customers get a job" description="Who starts a job and what one job covers." />
                <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label>Starts when</Label>
                        <Select value={listing.acquisition} onValueChange={(value) => onListingChange({ acquisition: value as Acquisition })}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {acquisitionOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{ACQUISITION_OPTIONS.find((option) => option.value === listing.acquisition)?.description}</p>
                    </div>
                    <div className="space-y-2">
                        <Label>One job per</Label>
                        <Select value={listing.scope} onValueChange={(value) => onListingChange({ scope: value as JobScope })}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SCOPE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{SCOPE_OPTIONS.find((option) => option.value === listing.scope)?.description}</p>
                    </div>
                    <div className="space-y-2">
                        <Label>Also start a job when an event arrives</Label>
                        <StartOnEvents value={listing.start_on_events} onChange={(next) => onListingChange({ start_on_events: next })} />
                        <p className="text-xs text-muted-foreground">
                            Events you send with the API (or Aimdoc&rsquo;s own) that create a job for the event&rsquo;s customer when they have none yet. A customer
                            who already has a job is left alone; routines on the Setup tab decide what wakes an existing job.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label>New jobs start in</Label>
                        <Select value={listing.default_job_mode} onValueChange={(value) => onListingChange({ default_job_mode: value as JobMode })}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {JOB_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{JOB_MODE_OPTIONS.find((option) => option.value === listing.default_job_mode)?.description} Each job can be switched later.</p>
                    </div>
                </div>
            </section>

            {listing.acquisition === 'stripe' && (
                <section className="mb-10">
                    <SectionHeader
                        title="Price"
                        description="One price per service, usually a subscription. Checkout creates the customer's job; it stays on while they pay. Activation creates the price in your Stripe account."
                    />
                    <ServicePricing serviceId={serviceId} />
                </section>
            )}

            <section className="mb-10">
                <SectionHeader title="Availability" />
                <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-1">
                        <Select value={status} onValueChange={(value) => onStatusChange(value as 'active' | 'disabled')}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="disabled">Disabled — no new jobs</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">Disabling blocks new assignments; the {jobsOn} job{jobsOn === 1 ? '' : 's'} already on keep running until turned off.</p>
                    </div>
                </div>
            </section>

            <section className="mb-16">
                <Separator className="my-6" />
                <div className="flex items-center justify-between rounded-lg border border-red-500/30 px-4 py-3">
                    <div>
                        <div className="text-sm font-semibold">Archive this service</div>
                        <div className="text-xs text-muted-foreground">Turns off every job of this service and hides it. History is kept.</div>
                    </div>
                    <Button variant="destructive" onClick={onDelete}>
                        Archive
                    </Button>
                </div>
            </section>
        </>
    );
};

export default ListingTab;
